import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import {
  Box,
  Button,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useAuth } from "@context/shared/AuthContext";
import { ROLE_NAMES, isAccreditor } from "@config/shared/roles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faStar } from "@fortawesome/pro-regular-svg-icons";

export const AccountPopover = (props) => {
  const { anchorEl, onClose, open, ...other } = props;
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const isClient = !location.pathname.includes("admin");
  const accreditor = isAccreditor(user?.roleId);

  const handleLogout = useCallback(async () => {
    try {
      onClose?.();
      await logout();
    } catch (err) {
      console.error(err);
      toast.error(t("error.wrong"));
    }
  }, [onClose, logout, t]);

  const getUserRoleName = (roleId) => {
    return ROLE_NAMES[roleId] || "member";
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "center",
        vertical: "bottom",
      }}
      disableScrollLock
      disableAutoFocus
      disableEnforceFocus
      onClose={onClose}
      open={!!open}
      slotProps={{ paper: { sx: { width: 220 } } }}
      {...other}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">
          {user?.name || t("dialogs.userFallback")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {getUserRoleName(user?.roleId)}
        </Typography>
      </Box>
      {!accreditor && (
        <>
          <Divider />
          <Box sx={{ p: 1 }}>
            <ListItemButton
              component={Link}
              to="/profile"
              onClick={() => {
                onClose?.();
              }}
              sx={{
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <ListItemIcon>
                <SvgIcon fontSize="small">
                  <FontAwesomeIcon icon={faUser} />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1">{t("client.profile")}</Typography>
                }
              />
            </ListItemButton>

            {isClient && (
              <ListItemButton
                component={Link}
                to="/favourites"
                onClick={() => {
                  onClose?.();
                }}
                sx={{
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                <ListItemIcon>
                  <SvgIcon fontSize="small">
                    <FontAwesomeIcon icon={faStar} />
                  </SvgIcon>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1">
                      {t("favourites.favouriteManager")}
                    </Typography>
                  }
                />
              </ListItemButton>
            )}
          </Box>
        </>
      )}
      <Divider sx={{ my: "0 !important" }} />
      <Box
        sx={{
          display: "flex",
          p: 1,
          justifyContent: "center",
        }}
      >
        <Button color="inherit" onClick={handleLogout} size="small">
          {t("common.logout")}
        </Button>
      </Box>
    </Popover>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
